import type { GraphicsBackendKind } from '@/graphics/GraphicsConfig'
import type { GSplatExportSource } from '@/plugins/gsplat/GSplatSystem'
import type {
  GaussianSplatExtraExporter,
  GSplatExtraExporterCapability,
  GSplatExtraExporterFormatTarget,
  GSplatExtraExporterResourceTarget,
} from '@/plugins/gsplat/types/PluginTypes'

const RESOURCE_CONTRACTS: Readonly<
  Record<
    GSplatExtraExporterResourceTarget,
    Readonly<{
      format: GSplatExtraExporterFormatTarget
      streaming: 'static' | 'lod'
      octree: GSplatExtraExporterCapability['octree']
    }>
  >
> = Object.freeze({
  GSplatResource: Object.freeze({ format: 'uncompressed', streaming: 'static', octree: 'none' }),
  GSplatCompressedResource: Object.freeze({
    format: 'compressed',
    streaming: 'static',
    octree: 'none',
  }),
  GSplatSogResource: Object.freeze({ format: 'sog', streaming: 'static', octree: 'none' }),
  GSplatOctreeResource: Object.freeze({ format: 'mixed', streaming: 'lod', octree: 'dataset' }),
})

/** WebGPU 自定义 exporter 的 Resource/Format/streaming/Octree 声明无效或不可用。 */
export class UnsupportedGSplatExtraExporterCapabilityError extends Error {
  readonly code = 'GSPLAT_EXTRA_EXPORTER_CAPABILITY_UNSUPPORTED'

  constructor(
    readonly exporterFormat: string,
    readonly backend: GraphicsBackendKind,
    readonly reason: string,
    readonly capability?: GSplatExtraExporterCapability
  ) {
    super(`3DGS 自定义 exporter "${exporterFormat}" 不支持 ${backend}：${reason}`)
    this.name = new.target.name
  }
}

function assertDeclaration(exporter: GaussianSplatExtraExporter): GSplatExtraExporterCapability {
  const declaration = exporter.gsplatCapability
  if (!declaration) {
    throw new UnsupportedGSplatExtraExporterCapabilityError(
      exporter.format,
      'webgpu',
      '缺少 gsplatCapability 的 Resource/Format/streaming/Octree 声明'
    )
  }
  const expected = RESOURCE_CONTRACTS[declaration.resource]
  if (
    !expected ||
    expected.format !== declaration.format ||
    expected.streaming !== declaration.streaming ||
    (declaration.resource === 'GSplatOctreeResource'
      ? declaration.octree === 'none'
      : declaration.octree !== 'none')
  ) {
    throw new UnsupportedGSplatExtraExporterCapabilityError(
      exporter.format,
      'webgpu',
      `声明组合无效，${declaration.resource} 必须使用 ${expected?.format ?? '未知'} / ${expected?.streaming ?? '未知'} / ${declaration.resource === 'GSplatOctreeResource' ? 'dataset|active-set|dataset-and-active-set' : 'none'}`,
      declaration
    )
  }
  return declaration
}

function formatMatches(
  declaration: GSplatExtraExporterCapability,
  source: GSplatExportSource
): boolean {
  const resource = source.resource.resourceKind
  const expectedResource =
    declaration.resource === 'GSplatResource'
      ? 'uncompressed'
      : declaration.resource === 'GSplatCompressedResource'
        ? 'compressed'
        : declaration.resource === 'GSplatSogResource'
          ? 'sog'
          : 'octree'
  if (resource !== expectedResource) return false
  if (source.streaming !== declaration.streaming) return false
  if (source.octreeSemantics !== declaration.octree) return false
  return declaration.format === 'mixed' || declaration.format === source.resourceKind
}

/** 在自定义 exporter 的副作用边界校验 WebGPU Unified 声明及实际 Resource 语义。 */
export function assertGSplatExtraExporterCapability(
  exporter: GaussianSplatExtraExporter,
  backend: GraphicsBackendKind,
  source?: GSplatExportSource
): void {
  if (backend === 'webgl2') return
  const declaration = assertDeclaration(exporter)
  if (source && !formatMatches(declaration, source)) {
    throw new UnsupportedGSplatExtraExporterCapabilityError(
      exporter.format,
      backend,
      `Resource 语义不匹配，实际为 ${source.resourceKind}/${source.streaming}/${source.octreeSemantics}`,
      declaration
    )
  }
}
